import {
  Category,
  CategoryElement,
  CategoryPayload,
} from "@/domain/category.interface";
import { axios, transformAxiosError } from "@/utils/axios.create";

const findByType = async ({
  type,
  status,
}: {
  type?: string;
  status: string;
}): Promise<Category> => {
  return axios
    .get("/categories/by-type", { params: { type } })
    .then((res) => {
      if (status === "ACTIVE" && res.data.body.categories) {
        const categories = res.data.body.categories;

        return {
          ...res.data.body,
          categories: categories.filter(
            (category: CategoryElement) => category.status === status
          ),
        };
      }

      return res.data.body;
    })
    .catch((err) => {
      throw new Error(
        err.response?.data?.message || "Failed to fetch categories"
      );
    });
};

export type CategoriesFindAllPayload = {
  sortField: string;
  sortOrder: number;
  pageNumber: number;
  pageSize: number;
  search: string;
  status: string;
  type: string;
};

const findAll = async ({
  sortField,
  sortOrder,
  pageNumber,
  pageSize,
  search,
  status,
  type,
}: CategoriesFindAllPayload): Promise<{
  list: Category[];
  totalCount: number;
}> => {
  const queryParams = new URLSearchParams({
    search,
    status,
    type,
    pageNumber: String(pageNumber + 1),
    pageSize: String(pageSize),
    sortField,
    sortOrder: sortOrder === 1 ? "asc" : "desc",
  });

  return axios
    .get(`/categories/search?${queryParams.toString()}`)
    .then((res) => ({
      list: res.data.body.list as Category[],
      totalCount: res.data.body.totalCount as number,
    }))
    .catch((err) => Promise.reject(transformAxiosError(err)));
};

const createOne = async (payload: CategoryPayload): Promise<Category> => {
  return axios
    .post("/categories", payload)
    .then((res) => res.data.body as Category)
    .catch((err) => Promise.reject(transformAxiosError(err)));
};

const updateOne = async ({
  id,
  payload,
}: {
  id: string;
  payload: CategoryPayload;
}): Promise<Category> => {
  return axios
    .put(`/categories/${id}`, payload)
    .then((res) => res.data.body as Category)
    .catch((err) => Promise.reject(transformAxiosError(err)));
};

const archiveOne = async (id: string): Promise<void> => {
  return axios
    .delete(`/categories/${id}`)
    .then(() => undefined)
    .catch((err) => Promise.reject(transformAxiosError(err)));
};

export const CategoryService = {
  findByType,
  findAll,
  createOne,
  updateOne,
  archiveOne,
};
